/**
 * @module nhcmap/layers/CurrentStormsLayer
 */
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { applyStyle } from 'ol-mapbox-style';

/**
 * @classdesc
 * Represents a CurrentStorms layer
 * This is a more specific layer class than many of the others because it is
 * based on a JSON file and much of the layer has to be built.
 */

class CurrentStormsLayer {
  /**
   * Create a Current Storms layer
   * @param {object} layerConfig 
   * @param {string} productId 
   */
  constructor(layerConfig, productId) {
    this.layerConfig = layerConfig;
    this.productId = productId;
  }

  /**
   * Load a Current Storms layer
   * @returns {Promise<object>} layer
   */
  async loadLayer() {
    //console.log("JSON Layer: " + this.layerConfig.id);
    // Load the data and create objects, then add points to the map 
    // (these are the points listing the current storm position)
    let layer;
    await this.loadStormData(this.layerConfig.url)
    .then(async storms => {
      const stormObjects = this.createStormObjects(storms);
      const features = this.makeStormFeatures(stormObjects);
      layer = new VectorLayer({
        source: new VectorSource({
          features: features
        }),
        visible: this.layerConfig.visible,
        zIndex: this.layerConfig.zIndex,        
      });
      const stylePath = `/maps/${this.productId}/styles/`;
      const styleFile = `${this.layerConfig.styleModule}.json`;
      const stylePathFile = `${stylePath}${styleFile}`;
      await applyStyle(layer, stylePathFile);
    });
    layer.set('name', this.layerConfig.id);
    return layer;
  }

  async updateLayer(layer) {
    const storms = await this.loadStormData(this.layerConfig.url);
    const stormObjects = this.createStormObjects(storms);
    const features = this.makeStormFeatures(stormObjects);
    const source = new VectorSource({
      features: features
    });
    layer.setSource(source);
  }

  async loadStormData(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok ' + response.statusText);
      }
      const data = await response.json();
      return data.activeStorms;
    } catch (error) {
      console.error('There has been a problem with your fetch operation:', error);
    }
  }

  /**
   * Take storm data in JSON format and create an array of storm objects with 
   * specific properties (e.g., id, latitude, longitude).
   * @param {object} storms 
   * @returns {array} - Array of storm objects
   */
  createStormObjects(storms) {
    const stormObjects = storms.map(storm => {
      return {
      id: storm.id,
      binNumber: storm.binNumber,
      name: storm.name,
      classification: storm.classification,
      intensity: storm.intensity,
      pressure: storm.pressure,
      latitude: storm.latitude,
      longitude: storm.longitude,
      latitudeNumeric: storm.latitudeNumeric,
      longitudeNumeric: storm.longitudeNumeric,
      movementDir: storm.movementDir,
      movementSpeed: storm.movementSpeed
      };
    });
    return stormObjects;
  }

  /**
   * Convert storm objects into OpenLayers Feature instances.
   * @param {array} stormObjects 
   */
  makeStormFeatures(stormObjects) {
    const features = stormObjects.map(storm => {
      const feature = new Feature({
        geometry: new Point(
          fromLonLat([storm.longitudeNumeric, storm.latitudeNumeric])
        ),
      });
      feature.setProperties(storm)
      return feature;
    });
    return features;
  }
}

export { CurrentStormsLayer };